import React from "react";
import { ResponsiveChartContainer } from "@mui/x-charts";
import { formatDateString } from "utils/date";
import { BarPlot, LinePlot } from "../../../components/Chart";
import * as Styled from "../styled";
import { CHART_TYPE_OPTIONS } from "./constants";
import { getMonthlyDataWithProjection } from "./utils";

interface Props {
  chartType: "line" | "bar";
  chartPeriod: "daily" | "monthly";
  seriesProps: any;
  yAxisProps?: any;
  dataset: any[];
  valueKeys?: string[];
  hideProjectionForKeys?: string[];
}

const Chart: React.FC<Props> = ({
  chartType,
  chartPeriod,
  dataset,
  valueKeys = ["value"],
  seriesProps,
  yAxisProps = {},
  hideProjectionForKeys = [],
}) => {
  const datasetWithProjection = getMonthlyDataWithProjection(
    dataset,
    valueKeys.filter((key) => !hideProjectionForKeys.includes(key))
  );

  const chart =
    CHART_TYPE_OPTIONS.find((o) => o.name === chartType) ||
    CHART_TYPE_OPTIONS[0];
  const ChartComponent = chart.component;

  const dateFormatter = (date) => {
    if (chartPeriod === "monthly") return formatDateString(date, "monthShort");

    return formatDateString(date, "numericShorter");
  };

  const series = valueKeys.map((key) => {
    const props = seriesProps[key] || seriesProps;
    return {
      ...props,
      dataKey: key,
      type: chartType,
    };
  });
  const xAxis: {
    scaleType: "band";
    dataKey: string;
    valueFormatter: (value: number) => string;
  }[] = [
    {
      scaleType: "band",
      dataKey: "date",
      valueFormatter: dateFormatter,
    },
  ];
  const yAxis = [
    {
      domainLimit: "strict",
      max:
        datasetWithProjection.reduce(
          (max, data) =>
            valueKeys.reduce(
              (localMax, key) => Math.max(localMax, data[key]),
              max
            ),
          0
        ) * 1.05,
      ...yAxisProps,
    },
  ];
  const chartProps = {
    series,
    xAxis,
    yAxis,
    height: 300,
  };

  return (
    <>
      {chartPeriod === "monthly" && (
        <Styled.ChartUnderlay>
          <ResponsiveChartContainer
            dataset={datasetWithProjection}
            {...chartProps}
          >
            <LinePlot />
            <BarPlot />
          </ResponsiveChartContainer>
        </Styled.ChartUnderlay>
      )}

      <Styled.ChartWrapper>
        <ChartComponent dataset={dataset} {...chartProps} />
      </Styled.ChartWrapper>
    </>
  );
};

export default Chart;
